<template>
  <section class="snowflake-reports-page">
    <div class="page-header">
      <div>
        <h1>Snowflake Reports</h1>
        <p class="page-copy">
          Generate Shopware order and return item feeds as saved CSV reports for Snowflake imports.
        </p>
      </div>

      <div class="page-actions">
        <button class="btn btn-outline-primary" type="button" :disabled="loadingReports" @click="loadReports">
          <i class="bi bi-arrow-clockwise" aria-hidden="true"></i>
          {{ loadingReports ? 'Loading...' : 'Refresh List' }}
        </button>
        <button class="btn btn-primary" type="button" :disabled="generating || !canGenerate" @click="generateReport">
          <i class="bi bi-filetype-csv" aria-hidden="true"></i>
          {{ generating ? 'Generating...' : 'Generate CSVs' }}
        </button>
      </div>
    </div>

    <div class="filter-panel">
      <div class="filter-grid">
        <div class="filter-field">
          <label class="form-label" for="snowflake-range-mode">Range Mode</label>
          <select id="snowflake-range-mode" v-model="rangeMode" class="form-select" :disabled="generating">
            <option value="yesterday">Yesterday</option>
            <option value="day">Specific Day</option>
            <option value="month">Specific Month</option>
            <option value="custom">Custom From/To</option>
          </select>
        </div>

        <div v-if="rangeMode === 'day'" class="filter-field">
          <label class="form-label" for="snowflake-day">Day</label>
          <input id="snowflake-day" v-model="specificDay" type="date" class="form-control" :disabled="generating" />
        </div>

        <div v-if="rangeMode === 'month'" class="filter-field">
          <label class="form-label" for="snowflake-month">Month</label>
          <input id="snowflake-month" v-model="selectedMonth" type="month" class="form-control" :disabled="generating" />
        </div>

        <template v-if="rangeMode === 'custom'">
          <div class="filter-field">
            <label class="form-label" for="snowflake-from">From</label>
            <input id="snowflake-from" v-model="customFrom" type="date" class="form-control" :disabled="generating" />
          </div>
          <div class="filter-field">
            <label class="form-label" for="snowflake-to">To</label>
            <input id="snowflake-to" v-model="customTo" type="date" class="form-control" :disabled="generating" />
          </div>
        </template>
      </div>
    </div>

    <div v-if="error" class="alert alert-danger">
      {{ error }}
    </div>

    <div v-if="infoMessage" class="alert alert-info">
      {{ infoMessage }}
    </div>

    <div class="meta-grid">
      <div class="meta-panel">
        <span class="meta-label">Selected Range</span>
        <strong>{{ rangeLabel }}</strong>
      </div>
      <div class="meta-panel">
        <span class="meta-label">Saved CSV Reports</span>
        <strong>{{ reports.length }}</strong>
      </div>
      <div class="meta-panel">
        <span class="meta-label">Selected Rows</span>
        <strong>{{ selectedReport?.row_count ?? 0 }}</strong>
      </div>
    </div>

    <div class="reports-panel">
      <div class="panel-header">
        <div>
          <h2>Generated CSV Reports</h2>
          <p class="query-meta">
            Generate and Refresh always write both Orders and Returns CSVs for the selected range.
          </p>
        </div>
      </div>

      <div v-if="loadingReports" class="loading-state">
        Loading saved CSV reports...
      </div>

      <div v-else-if="reports.length === 0" class="empty-state">
        No Snowflake CSV reports have been generated yet.
      </div>

      <div v-else class="table-container">
        <table class="table align-middle">
          <thead>
            <tr>
              <th>Report</th>
              <th>Range</th>
              <th>Type</th>
              <th>Generation</th>
              <th>Upload</th>
              <th>Generated At</th>
              <th>Uploaded At</th>
              <th>Rows</th>
              <th>Size</th>
              <th class="actions-column">Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="report in reports" :key="report.id" :class="{ 'is-selected': report.id === selectedReportId }">
              <td>
                <strong>{{ report.file_name }}</strong>
                <div class="table-subtext">{{ report.label }}</div>
              </td>
              <td>{{ formatRange(report.range) }}</td>
              <td>{{ generationTypeLabel(report.generation_type) }}</td>
              <td>
                <span class="status-pill" :class="generationStatusClass(report.generation_status)">
                  {{ statusLabel(report.generation_status) }}
                </span>
                <div v-if="report.generation_error" class="table-warning">{{ report.generation_error }}</div>
              </td>
              <td>
                <span class="status-pill" :class="uploadStatusClass(report.upload_status)">
                  {{ uploadStatusLabel(report.upload_status) }}
                </span>
                <div v-if="report.upload_error" class="table-warning">{{ report.upload_error }}</div>
                <div v-if="report.s3_key" class="table-subtext">{{ report.s3_key }}</div>
              </td>
              <td>{{ formatTimestamp(report.generated_at) }}</td>
              <td>{{ formatTimestamp(report.uploaded_at) }}</td>
              <td>{{ report.row_count ?? 0 }}</td>
              <td>{{ formatFileSize(report.csv_size) }}</td>
              <td class="actions-column">
                <button class="btn btn-sm btn-outline-secondary" type="button" title="View report" :disabled="busyReportId === report.id" @click="viewReport(report.id)">
                  <i class="bi bi-eye" aria-hidden="true"></i>
                  View
                </button>
                <button class="btn btn-sm btn-outline-primary" type="button" title="Re-generate both CSVs for this range" :disabled="generating || busyReportId === report.id" @click="refreshReport(report.id)">
                  <i class="bi bi-arrow-repeat" aria-hidden="true"></i>
                  Re-generate
                </button>
                <button class="btn btn-sm btn-outline-success" type="button" title="Re-upload both CSVs for this range" :disabled="uploading || report.generation_status !== 'success' || busyReportId === report.id" @click="uploadReport(report.id)">
                  <i class="bi bi-cloud-arrow-up" aria-hidden="true"></i>
                  Re-upload
                </button>
                <button class="btn btn-sm btn-outline-dark" type="button" title="Download CSV" :disabled="report.generation_status !== 'success' || busyReportId === report.id" @click="downloadReport(report)">
                  <i class="bi bi-download" aria-hidden="true"></i>
                  Download
                </button>
                <button class="btn btn-sm btn-outline-danger" type="button" title="Delete local manual CSVs for this range" :disabled="deleting || report.generation_type !== 'manually' || busyReportId === report.id" @click="deleteReport(report)">
                  <i class="bi bi-trash" aria-hidden="true"></i>
                  Delete
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <div v-if="selectedReport" class="reports-panel">
      <div class="panel-header">
        <div>
          <h2>{{ selectedReport.file_name }}</h2>
          <p class="query-meta">
            {{ formatRange(selectedReport.range) }} · {{ selectedReport.row_count }} rows
          </p>
        </div>
        <button class="btn btn-outline-dark" type="button" title="Download selected CSV" :disabled="selectedReport.generation_status !== 'success'" @click="downloadReport(selectedReport)">
          <i class="bi bi-download" aria-hidden="true"></i>
          Download CSV
        </button>
      </div>

      <div v-if="selectedRows.length === 0" class="empty-state">
        The selected CSV report contains no rows.
      </div>

      <div v-else class="table-container detail-table-container">
        <table class="table table-sm table-hover align-middle">
          <thead>
            <tr>
              <th v-for="column in selectedColumns" :key="column">{{ column }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(row, rowIndex) in selectedRows" :key="rowIndex">
              <td v-for="column in selectedColumns" :key="`${rowIndex}-${column}`">
                {{ formatValue(row[column]) }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </section>
</template>

<script>
import api from '@/utils/api'

export default {
  name: 'SnowflakeReportsView',
  data() {
    const defaults = this.getDefaultDates()

    return {
      rangeMode: 'yesterday',
      specificDay: defaults.yesterday,
      selectedMonth: defaults.month,
      customFrom: defaults.yesterday,
      customTo: defaults.yesterday,
      reports: [],
      selectedReport: null,
      selectedReportId: '',
      loadingReports: false,
      generating: false,
      uploading: false,
      deleting: false,
      busyReportId: '',
      error: null,
      infoMessage: ''
    }
  },
  computed: {
    selectedRows() {
      return Array.isArray(this.selectedReport?.rows) ? this.selectedReport.rows : []
    },
    selectedColumns() {
      return Array.isArray(this.selectedReport?.columns) ? this.selectedReport.columns : []
    },
    canGenerate() {
      if (this.rangeMode === 'day') {
        return Boolean(this.specificDay)
      }

      if (this.rangeMode === 'month') {
        return Boolean(this.selectedMonth)
      }

      if (this.rangeMode === 'custom') {
        return Boolean(this.customFrom && this.customTo)
      }

      return true
    },
    rangeLabel() {
      if (this.rangeMode === 'day') {
        return this.specificDay || '-'
      }

      if (this.rangeMode === 'month') {
        return this.selectedMonth || '-'
      }

      if (this.rangeMode === 'custom') {
        return `${this.customFrom || '-'} to ${this.customTo || '-'}`
      }

      return 'Yesterday'
    }
  },
  async mounted() {
    await this.loadReports()
  },
  methods: {
    getDefaultDates() {
      const yesterday = new Date()
      yesterday.setDate(yesterday.getDate() - 1)

      const year = yesterday.getFullYear()
      const month = String(yesterday.getMonth() + 1).padStart(2, '0')
      const day = String(yesterday.getDate()).padStart(2, '0')

      return {
        yesterday: `${year}-${month}-${day}`,
        month: `${year}-${month}`
      }
    },
    buildRangeParams() {
      if (this.rangeMode === 'day') {
        return {
          mode: 'day',
          day: this.specificDay
        }
      }

      if (this.rangeMode === 'month') {
        return {
          mode: 'month',
          month: this.selectedMonth
        }
      }

      if (this.rangeMode === 'custom') {
        return {
          mode: 'custom',
          from: this.customFrom,
          to: this.customTo
        }
      }

      return {
        mode: 'yesterday'
      }
    },
    async loadReports() {
      this.loadingReports = true
      this.error = null

      try {
        const response = await api.get('/api/endpoints/snowflake-reports.php', {
          params: { action: 'list' }
        })

        if (!response.data?.success) {
          throw new Error(response.data?.error || 'Failed to load Snowflake CSV reports.')
        }

        this.reports = Array.isArray(response.data.data?.reports) ? response.data.data.reports : []
      } catch (error) {
        this.reports = []
        this.error = error?.response?.data?.error || error.message || 'Failed to load Snowflake CSV reports.'
      } finally {
        this.loadingReports = false
      }
    },
    async generateReport() {
      this.generating = true
      this.error = null
      this.infoMessage = ''

      try {
        const response = await api.get('/api/endpoints/snowflake-reports.php', {
          params: {
            action: 'generate',
            ...this.buildRangeParams()
          },
          timeout: 120000
        })

        this.applyReportResponse(response, 'Snowflake Orders and Returns CSV reports generated.')
      } catch (error) {
        this.error = error?.response?.data?.error || error.message || 'Failed to generate Snowflake CSV report.'
      } finally {
        this.generating = false
      }
    },
    async viewReport(reportId) {
      if (!reportId) {
        return
      }

      this.busyReportId = reportId
      this.error = null
      this.infoMessage = ''

      try {
        const response = await api.get('/api/endpoints/snowflake-reports.php', {
          params: {
            action: 'view',
            id: reportId
          }
        })

        this.applyReportResponse(response)
      } catch (error) {
        this.error = error?.response?.data?.error || error.message || 'Failed to load Snowflake CSV report.'
      } finally {
        this.busyReportId = ''
      }
    },
    async refreshReport(reportId) {
      if (!reportId) {
        return
      }

      this.generating = true
      this.busyReportId = reportId
      this.error = null
      this.infoMessage = ''

      try {
        const response = await api.get('/api/endpoints/snowflake-reports.php', {
          params: {
            action: 'refresh',
            id: reportId
          },
          timeout: 120000
        })

        this.applyReportResponse(response, 'Snowflake Orders and Returns CSV reports refreshed.')
      } catch (error) {
        this.error = error?.response?.data?.error || error.message || 'Failed to refresh Snowflake CSV report.'
      } finally {
        this.generating = false
        this.busyReportId = ''
      }
    },
    async uploadReport(reportId) {
      if (!reportId) {
        return
      }

      this.uploading = true
      this.busyReportId = reportId
      this.error = null
      this.infoMessage = ''

      try {
        const response = await api.get('/api/endpoints/snowflake-reports.php', {
          params: {
            action: 'upload',
            id: reportId
          },
          timeout: 120000
        })

        this.applyReportResponse(response, 'Snowflake Orders and Returns CSV reports uploaded.')
      } catch (error) {
        this.error = error?.response?.data?.error || error.message || 'Failed to upload Snowflake CSV reports.'
      } finally {
        this.uploading = false
        this.busyReportId = ''
      }
    },
    async deleteReport(report) {
      const reportId = report?.id
      if (!reportId || report?.generation_type !== 'manually') {
        return
      }

      const confirmed = window.confirm('Delete the locally saved manual Orders and Returns CSV files for this range? Uploaded S3 files are not changed.')
      if (!confirmed) {
        return
      }

      this.deleting = true
      this.busyReportId = reportId
      this.error = null
      this.infoMessage = ''

      try {
        const response = await api.get('/api/endpoints/snowflake-reports.php', {
          params: {
            action: 'delete',
            id: reportId
          }
        })

        this.applyReportResponse(response, 'Manual local Snowflake CSV files deleted.')
      } catch (error) {
        this.error = error?.response?.data?.error || error.message || 'Failed to delete local Snowflake CSV reports.'
      } finally {
        this.deleting = false
        this.busyReportId = ''
      }
    },
    async downloadReport(report) {
      const reportId = report?.id
      if (!reportId) {
        return
      }

      this.busyReportId = reportId
      this.error = null

      try {
        const response = await api.get('/api/endpoints/snowflake-report-download.php', {
          params: { id: reportId },
          responseType: 'blob',
          timeout: 120000
        })

        const blobUrl = URL.createObjectURL(response.data)
        const disposition = response.headers['content-disposition'] || ''
        const matchedFileName = disposition.match(/filename="?([^";]+)"?/i)
        const fileName = matchedFileName?.[1] || report.file_name || `${reportId}.csv`
        const link = document.createElement('a')
        link.href = blobUrl
        link.download = fileName
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        URL.revokeObjectURL(blobUrl)
      } catch (error) {
        this.error = error?.response?.data?.error || error.message || 'Failed to download Snowflake CSV report.'
      } finally {
        this.busyReportId = ''
      }
    },
    applyReportResponse(response, message = '') {
      if (!response.data?.success) {
        throw new Error(response.data?.error || 'Snowflake report request failed.')
      }

      const payload = response.data.data || {}
      this.reports = Array.isArray(payload.reports) ? payload.reports : []
      this.selectedReport = payload.report || null
      this.selectedReportId = this.selectedReport?.id || ''
      this.infoMessage = message
    },
    formatRange(range) {
      if (!range) {
        return '-'
      }

      return range.label || (range.from === range.to ? range.from : `${range.from} to ${range.to}`)
    },
    formatTimestamp(value) {
      if (!value) {
        return '-'
      }

      return String(value).replace('T', ' ').replace('+02:00', '').replace('+01:00', '').replace('+00:00', '')
    },
    formatFileSize(value) {
      const bytes = Number(value || 0)
      if (bytes < 1024) {
        return `${bytes} B`
      }

      if (bytes < 1024 * 1024) {
        return `${(bytes / 1024).toFixed(1)} KB`
      }

      return `${(bytes / 1024 / 1024).toFixed(1)} MB`
    },
    formatValue(value) {
      if (value === null || value === undefined || value === '') {
        return '-'
      }

      return String(value)
    },
    generationTypeLabel(value) {
      return value === 'auto' ? 'Auto' : 'Manual'
    },
    statusLabel(value) {
      return {
        success: 'Success',
        failed: 'Failed'
      }[value] || 'Unknown'
    },
    uploadStatusLabel(value) {
      return {
        not_uploaded: 'Not Uploaded',
        uploading: 'Uploading',
        success: 'Uploaded',
        failed: 'Failed'
      }[value] || 'Unknown'
    },
    generationStatusClass(value) {
      return {
        success: 'status-success',
        failed: 'status-failed'
      }[value] || 'status-muted'
    },
    uploadStatusClass(value) {
      return {
        not_uploaded: 'status-muted',
        uploading: 'status-pending',
        success: 'status-success',
        failed: 'status-failed'
      }[value] || 'status-muted'
    }
  }
}
</script>

<style scoped>
.snowflake-reports-page {
  max-width: 1600px;
  margin: 0 auto;
}

.page-header {
  display: flex;
  justify-content: space-between;
  gap: 1.5rem;
  align-items: flex-start;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
}

.page-header h1 {
  margin-bottom: 0.35rem;
}

.page-copy,
.query-meta,
.table-subtext {
  color: #475569;
}

.page-copy,
.query-meta {
  margin: 0;
}

.page-actions {
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
}

.page-actions .btn,
.actions-column .btn,
.panel-header .btn {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
}

.filter-panel,
.meta-panel,
.reports-panel,
.empty-state,
.loading-state {
  border: 1px solid #dbe3ef;
  border-radius: 0.5rem;
  background: #ffffff;
  box-shadow: 0 0.75rem 2rem rgba(15, 23, 42, 0.05);
}

.filter-panel {
  padding: 1.25rem;
  margin-bottom: 1.5rem;
}

.filter-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 1rem;
}

.filter-field {
  min-width: 0;
}

.meta-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 1rem;
  margin-bottom: 1.5rem;
}

.meta-panel {
  padding: 1rem 1.15rem;
}

.meta-label {
  display: block;
  margin-bottom: 0.35rem;
  color: #64748b;
  font-size: 0.82rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.reports-panel {
  padding: 1.25rem;
  margin-bottom: 1.5rem;
}

.panel-header {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  align-items: flex-start;
  margin-bottom: 1rem;
  flex-wrap: wrap;
}

.panel-header h2 {
  margin: 0 0 0.25rem;
  font-size: 1.2rem;
}

.loading-state,
.empty-state {
  padding: 1.5rem;
}

.table-container {
  overflow: auto;
}

.detail-table-container {
  max-height: 540px;
}

.table {
  margin-bottom: 0;
  min-width: max-content;
}

.table th {
  white-space: nowrap;
}

.table td {
  vertical-align: top;
}

.table-subtext {
  font-size: 0.9rem;
  margin-top: 0.2rem;
}

.table-warning {
  color: #b45309;
  font-size: 0.85rem;
  margin-top: 0.25rem;
  max-width: 22rem;
  white-space: normal;
}

.status-pill {
  display: inline-flex;
  align-items: center;
  padding: 0.25rem 0.65rem;
  border-radius: 999px;
  font-size: 0.82rem;
  font-weight: 600;
  white-space: nowrap;
}

.status-success {
  background: #dcfce7;
  color: #166534;
}

.status-failed {
  background: #fee2e2;
  color: #991b1b;
}

.status-pending {
  background: #dbeafe;
  color: #1d4ed8;
}

.status-muted {
  background: #f1f5f9;
  color: #475569;
}

.actions-column {
  white-space: nowrap;
}

.actions-column .btn {
  margin-right: 0.4rem;
}

.actions-column .btn:last-child {
  margin-right: 0;
}

.is-selected {
  background: rgba(59, 130, 246, 0.08);
}

@media (max-width: 768px) {
  .page-actions,
  .page-actions .btn {
    width: 100%;
  }
}
</style>
